type AnyRecord = Record<string, unknown>;

const hasOwnProperty = Object.prototype.hasOwnProperty;

const isPlainRecord = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') {
    return false;
  }

  if (Array.isArray(value)) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

type DiffPath = readonly string[];
type DiffResult = Readonly<{ changed: true; value: unknown } | { changed: false }>;

const changed = (value: unknown): DiffResult => ({ changed: true, value });
const unchanged = (): DiffResult => ({ changed: false });

export const DELETION_MARKER_FLAG = '_d_';

type DeletionMarker = {
  [DELETION_MARKER_FLAG]: true;
};

const createDeletionMarker = (): DeletionMarker => ({
  [DELETION_MARKER_FLAG]: true,
});

const isDeletionMarker = (value: unknown): value is DeletionMarker =>
  isPlainRecord(value) && value[DELETION_MARKER_FLAG] === true;

const sanitizeDeletionMarkers = (input: unknown): unknown => {
  if (isDeletionMarker(input) || input === undefined) {
    return undefined;
  }

  if (Array.isArray(input)) {
    return input.map((entry) => sanitizeDeletionMarkers(entry));
  }

  if (isPlainRecord(input)) {
    const result: AnyRecord = {};

    for (const [key, value] of Object.entries(input as AnyRecord)) {
      if (isDeletionMarker(value) || value === undefined) {
        continue;
      }

      const sanitizedValue = sanitizeDeletionMarkers(value);
      if (sanitizedValue !== undefined) {
        result[key] = sanitizedValue;
      }
    }

    return result;
  }

  return input;
};

export const encodeDeletionMarkers = <T>(value: T): T => {
  const encode = (input: unknown): unknown => {
    if (Array.isArray(input)) {
      return input.map((entry) => encode(entry));
    }

    if (input instanceof Date) {
      return input.toISOString();
    }

    if (isPlainRecord(input)) {
      const result: AnyRecord = {};

      for (const [key, nestedValue] of Object.entries(input as AnyRecord)) {
        if (nestedValue === undefined) {
          result[key] = createDeletionMarker();
          continue;
        }

        result[key] = encode(nestedValue);
      }

      return result;
    }

    return input;
  };

  return encode(value) as T;
};

const valuesAreEqual = (previousValue: unknown, nextValue: unknown): boolean => {
  if (Object.is(previousValue, nextValue)) {
    return true;
  }

  if (previousValue === null || nextValue === null) {
    return previousValue === nextValue;
  }

  if (previousValue instanceof Date && nextValue instanceof Date) {
    return previousValue.getTime() === nextValue.getTime();
  }

  if (Array.isArray(previousValue) && Array.isArray(nextValue)) {
    if (previousValue.length !== nextValue.length) {
      return false;
    }

    for (let index = 0; index < previousValue.length; index++) {
      if (!valuesAreEqual(previousValue[index], nextValue[index])) {
        return false;
      }
    }

    return true;
  }

  if (isPlainRecord(previousValue) && isPlainRecord(nextValue)) {
    const previousKeys = Object.keys(previousValue as AnyRecord);
    const nextKeys = Object.keys(nextValue as AnyRecord);

    if (previousKeys.length !== nextKeys.length) {
      return false;
    }

    for (const key of previousKeys) {
      if (!hasOwnProperty.call(nextValue as AnyRecord, key)) {
        return false;
      }

      if (!valuesAreEqual((previousValue as AnyRecord)[key], (nextValue as AnyRecord)[key])) {
        return false;
      }
    }

    return true;
  }

  return false;
};

const shouldReplaceEntireValue = (path: DiffPath, atomicPaths?: ReadonlySet<string>): boolean => {
  if (!atomicPaths || path.length === 0) {
    return false;
  }

  return atomicPaths.has(path.join('.'));
};

const computeDiffAtPath = (
  previousValue: unknown,
  nextValue: unknown,
  path: DiffPath,
  atomicPaths?: ReadonlySet<string>,
): DiffResult => {
  if (valuesAreEqual(previousValue, nextValue)) {
    return unchanged();
  }

  if (shouldReplaceEntireValue(path, atomicPaths)) {
    return changed(nextValue);
  }

  if (isPlainRecord(previousValue) && isPlainRecord(nextValue)) {
    return computeObjectDiff(previousValue as AnyRecord, nextValue as AnyRecord, path, atomicPaths);
  }

  if (Array.isArray(previousValue) && Array.isArray(nextValue)) {
    return computeArrayDiff(previousValue, nextValue, path, atomicPaths);
  }

  return changed(nextValue);
};

const computeObjectDiff = (
  previousValue: AnyRecord,
  nextValue: AnyRecord,
  path: DiffPath,
  atomicPaths?: ReadonlySet<string>,
): DiffResult => {
  const delta: AnyRecord = {};
  let localChange = false;

  // Check all keys from both previous and next to detect deletions
  const allKeys = new Set([...Object.keys(previousValue), ...Object.keys(nextValue)]);

  for (const key of allKeys) {
    const previousHasKey = hasOwnProperty.call(previousValue, key);
    const nextHasKey = hasOwnProperty.call(nextValue, key);
    const nextEntry = nextValue[key];

    // Key removed entirely
    if (previousHasKey && !nextHasKey) {
      delta[key] = createDeletionMarker();
      localChange = true;
      continue;
    }

    // Treat explicit `undefined` as a deletion (JSON would drop it otherwise)
    if (previousHasKey && nextHasKey && nextEntry === undefined) {
      delta[key] = createDeletionMarker();
      localChange = true;
      continue;
    }

    // Key was added
    if (!previousHasKey && nextHasKey) {
      delta[key] = nextValue[key];
      localChange = true;
      continue;
    }

    // Key exists in both - check if value changed
    const childDiff = computeDiffAtPath(previousValue[key], nextValue[key], [...path, key], atomicPaths);

    if (childDiff.changed) {
      delta[key] = childDiff.value;
      localChange = true;
    }
  }

  return localChange ? changed(delta) : unchanged();
};

const computeArrayDiff = (
  previousValue: unknown[],
  nextValue: unknown[],
  path: DiffPath,
  atomicPaths?: ReadonlySet<string>,
): DiffResult => {
  if (previousValue.length !== nextValue.length) {
    return changed(nextValue);
  }

  for (let index = 0; index < previousValue.length; index++) {
    const elementDiff = computeDiffAtPath(
      previousValue[index],
      nextValue[index],
      [...path, String(index)],
      atomicPaths,
    );

    if (elementDiff.changed) {
      return changed(nextValue);
    }
  }

  return unchanged();
};

export interface ComputeDiffOptions {
  atomicReplacePaths?: Iterable<string>;
}

export const computeDiff = <T extends object>(
  previous: T | undefined,
  next: T,
  options?: ComputeDiffOptions,
): Partial<T> | null => {
  if (!previous) {
    return next;
  }

  const atomicPaths =
    options?.atomicReplacePaths !== undefined ? new Set<string>(options.atomicReplacePaths) : undefined;

  const result = computeDiffAtPath(previous, next, [], atomicPaths);
  return result.changed ? (result.value as Partial<T>) : null;
};

const deepMerge = (current: unknown, incoming: unknown): unknown => {
  if (isDeletionMarker(incoming) || incoming === undefined) {
    return undefined;
  }

  if (Array.isArray(current) && Array.isArray(incoming)) {
    const currentArray = current as unknown[];
    const incomingArray = incoming as unknown[];

    return incomingArray.map((value, index) => {
      const existingValue = currentArray[index];
      const sanitizedIncoming = sanitizeDeletionMarkers(value);

      if (isPlainRecord(existingValue) && isPlainRecord(sanitizedIncoming)) {
        return deepMerge(existingValue, sanitizedIncoming);
      }

      return sanitizedIncoming;
    });
  }

  if (!isPlainRecord(current) || !isPlainRecord(incoming)) {
    return sanitizeDeletionMarkers(incoming);
  }

  const result: AnyRecord = { ...(current as AnyRecord) };

  for (const [key, value] of Object.entries(incoming)) {
    if (isDeletionMarker(value) || value === undefined) {
      delete result[key];
      continue;
    }

    const sanitizedValue = sanitizeDeletionMarkers(value);
    const existingValue = (current as AnyRecord)[key];

    if (sanitizedValue === undefined) {
      delete result[key];
      continue;
    }

    if (isPlainRecord(existingValue) && isPlainRecord(sanitizedValue)) {
      result[key] = deepMerge(existingValue, sanitizedValue);
    } else {
      result[key] = sanitizedValue;
    }
  }

  return result;
};

export const applyTableStateDiff = <T extends object>(base: T, diff?: Partial<T> | null): T => {
  if (!diff) {
    return base;
  }

  return deepMerge(base, diff) as T;
};

export const reconstructTableStatesFromDiffs = <T extends object>(compressedStates: Partial<T>[]): T[] => {
  if (!compressedStates.length) {
    return [];
  }

  const reconstructed: T[] = [];
  let current = compressedStates[0] as T;
  reconstructed.push(current);

  for (let index = 1; index < compressedStates.length; index++) {
    const snapshot = compressedStates[index];
    const isFullSnapshot =
      isPlainRecord(snapshot) && Object.prototype.hasOwnProperty.call(snapshot as Record<string, unknown>, 'tableId');

    if (isFullSnapshot) {
      // Legacy storage wrote full table states on every entry, which always include `tableId`.
      current = snapshot as T;
    } else {
      // New compressed storage writes diffs only, so merge them into the running snapshot.
      current = applyTableStateDiff(current, snapshot);
    }

    reconstructed.push(current);
  }

  return reconstructed;
};

/* -------------------------------------------------------------------------- */
/*                             Table-State Helpers                             */
/* -------------------------------------------------------------------------- */

export const TABLE_STATE_ATOMIC_PATHS = ['playerSeat.preActionsAvailable'] as const;

export const TABLE_STATE_DIFF_OPTIONS: ComputeDiffOptions = {
  atomicReplacePaths: TABLE_STATE_ATOMIC_PATHS,
};

export const computeTableStateDiff = <T extends object>(previous: T | undefined, next: T): Partial<T> | null =>
  computeDiff<T>(previous, next, TABLE_STATE_DIFF_OPTIONS);
